import React from 'react';
import styled from 'styled-components';

import {
  Heading,
  LinkButton,
  PrimaryGhostLinkButton,
  PrimaryLinkButton,
  SecondaryGhostLinkButton,
  SecondaryLinkButton,
  WhiteGhostLinkButton,
} from '../CommonStyledComponents';
import media from '../../utils/media';
import circleFernOcean from '/src/assets/icons/circle-fern-decoration-ocean.webp';
import circleFernSienna from '/src/assets/icons/circle-fern-decoration-sienna.webp';
import featuresOnboarding from '/src/assets/images/features/property-management-system/features_onboarding.webp';
import channelUp from '/src/assets/icons/channel-up.webp';

import { Link } from 'gatsby-plugin-react-i18next';

// The  mapping object for assets.
const imageMap = {
  sienna: circleFernSienna,
  ocean: circleFernOcean,
  feature: featuresOnboarding,
  channel: channelUp,
};

// Intrinsic pixel dimensions of each decoration image, so the browser can
// reserve the correct aspect-ratio box before the image loads (avoids CLS).
// Values match the actual asset sizes; CSS max-width/max-height still control
// the rendered size, so this does not change how the image displays.
const imageDimensionsMap = {
  sienna: { width: 830, height: 634 },
  ocean: { width: 830, height: 634 },
  feature: { width: 1047, height: 705 },
  channel: { width: 788, height: 746 },
};

const backgroundMap = {
  sienna:
    'linear-gradient(0deg, #B94D00 0%, #B94D00 100%), linear-gradient(86deg, #B94D00 0%, #FD6300 100%)',
  ocean:
    'linear-gradient(0deg, #008380 0%, #008380 100%), linear-gradient(86deg, #008380 0%, #00B5B2 100%)',
  feature: '#FFE9DE',
  channel: '#DFF9F8',
};

const CTASectionContainer = styled.div`
  display: flex;
  justify-content: center;

  padding: ${(props) => props.$containerPadding ?? '0 1.8125rem 7.5rem'};

  ${media.laptop`
    padding: ${(props) =>
      props.$laptopContainerPadding ??
      props.$containerPadding ??
      '0 1.8125rem 7.5rem'};
  `}
`;

const CTABox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 93.75rem;

  border-radius: 1.25rem;
  background: ${(props) => backgroundMap[props.$background]};

  padding: ${(props) => props.$boxPadding ?? '4.375rem 1.938rem 0rem'};

  ${(props) =>
    !props.$boxPadding &&
    `padding-bottom: ${props.$background === 'ocean' ? '3.25rem' : '0'};`}

  ${media.laptop`
    flex-direction: row;
    align-items: initial;
    padding: ${(props) =>
      props.$laptopBoxPadding ?? '5.5625rem 10.125rem 5.625rem 5rem'};
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9375rem;

  > a {
    width: 100%;
  }

  ${media.phone`
    flex-direction: row;

    > a {
      width: auto;
    }
  `}
`;

const CTADecoration = styled.div`
  display: flex;
  flex-direction: row-reverse;

  > div > img {
    max-width: ${(props) => (props.color === 'feature' ? '22.5rem' : '20rem')};
    max-height: ${(props) =>
      props.color === 'feature' ? '16.5rem' : '15.48438rem'};
    opacity: ${(props) => (props.color === 'feature' ? '1' : '0.5')};
    object-fit: ${(props) => (props.color === 'feature' ? 'cover' : 'fill')};
    margin-top: ${(props) =>
      props.color === 'feature'
        ? '4.6875rem'
        : props.color === 'channel'
          ? '69px'
          : '0rem'};

    margin-bottom: ${(props) =>
      props.color === 'feature'
        ? '-3px'
        : props.color === 'channel'
          ? '-4px'
          : '0px'};
  }

  ${media.laptop`
    width: 45.938rem;

    > div {
      position: absolute;

    }

    > div > img {
    
      max-width: ${(props) =>
        props.color === 'feature' ? '32.7162rem' : '25.9375rem;'};
      max-height: ${(props) =>
        props.color === 'feature' ? '19.8125rem' : '19.8125rem;'};

      position: absolute;
      top: ${(props) =>
        props.color === 'feature'
          ? '-1.9rem'
          : props.color === 'channel'
            ? '0.7rem'
            : '-5.5625rem'};
      left: ${(props) =>
        props.color === 'channel' ? '-19.9375rem' : '-25.9375rem'};
      margin-top: ${(props) =>
        props.color === 'feature' ? '-0.6875rem' : '0rem'};

    }
  `}
`;

const DescriptionText = styled.p`
  color: ${(props) => props.$textColor ?? '#2D2926'};
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin: -2rem 0 2rem;

  /* */
  ${media.laptop`
    font-size: 1.125rem; 
    text-align: left;
    margin: -2.5rem 0 2.5rem; 
  `}
`;

const CTASection = ({
  ctaHeading,
  descriptionText,
  color,
  contentMaxWidth = '45.938rem',
  textColor,
  fontSize,
  laptopFontSize,
  textAlign,
  containerPadding,
  laptopContainerPadding,
  boxPadding,
  laptopBoxPadding,
  // Primary Button Props
  primaryButtonText,
  primaryButtonLink, //Will hold the URL (e.g., "/pricing" or "#get-quote")
  onButtonClick,
  // Secondary Button Props
  secondaryButtonText,
  secondaryButtonLink, //URL for the second button
  onSecondaryClick,
}) => {
  // The following determines the button color
  const PrimaryButton =
    color === 'channel'
      ? SecondaryLinkButton
      : color === 'sienna' || color === 'feature'
        ? PrimaryGhostLinkButton
        : SecondaryGhostLinkButton;

  return (
    <CTASectionContainer
      $containerPadding={containerPadding}
      $laptopContainerPadding={laptopContainerPadding}
    >
      <CTABox
        $background={color}
        $boxPadding={boxPadding}
        $laptopBoxPadding={laptopBoxPadding}
      >
        <div style={{ maxWidth: contentMaxWidth }}>
          <Heading
            $textAlign={textAlign}
            $lineHeight="135%"
            $fontSize={fontSize || '1.875rem'}
            $fontWeight="600"
            $laptopFontSize={laptopFontSize || '2.5rem'}
            $margin="0 0 3.125rem"
            $laptopMargin="0 0 3.75rem"
            $color={textColor || '#FFF'}
          >
            {ctaHeading}
          </Heading>
          {descriptionText && (
            <DescriptionText $textColor="#2D2926">
              {descriptionText}
            </DescriptionText>
          )}
          <ButtonGroup data-ga-cta-location="body">
            {/* Primary Button */}
            {primaryButtonText && (
              <PrimaryButton
                as={Link}
                to={primaryButtonLink}
                $compact
                onClick={onButtonClick}
              >
                {primaryButtonText}
              </PrimaryButton>
            )}

            {/* Secondary Button (renders only if text is provided) */}
            {secondaryButtonText && (
              <WhiteGhostLinkButton
                as={Link}
                to={secondaryButtonLink} // DYNAMIC: Uses the new prop
                $compact
                $outlined
                onClick={onSecondaryClick}
              >
                {secondaryButtonText}
              </WhiteGhostLinkButton>
            )}
          </ButtonGroup>
        </div>
        <CTADecoration color={color}>
          <div>
            <img
              src={imageMap[color]}
              alt=""
              width={imageDimensionsMap[color]?.width}
              height={imageDimensionsMap[color]?.height}
            />
          </div>
        </CTADecoration>
      </CTABox>
    </CTASectionContainer>
  );
};

export default CTASection;
